import {
	BikeIcon,
	ChevronRightIcon,
	HandIcon,
	PackageIcon,
	TopPharmacyBadge,
} from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import { clsx } from "clsx";
import { Price } from "~/components/price.component";

export type OfferCardDeliveryKind = "shipping" | "pickup" | "courier";

export type OfferCardDeliveryBadge = {
	kind: OfferCardDeliveryKind;
	label?: string;
};

export type OfferCardDiscount = {
	originalPrice: number;
	percentage: number;
};

export type OfferCardAddress = {
	street?: string;
	postalCode?: string;
	city?: string;
};

export type OfferCardViewModel = {
	pharmacyName: string;
	price?: number;
	address?: OfferCardAddress;
	distanceLabel?: string;
	deliveryBadges?: OfferCardDeliveryBadge[];
	discount?: OfferCardDiscount;
	featured?: boolean;
};

const getDeliveryIcon = (kind: OfferCardDeliveryKind) => {
	if (kind === "shipping") return <PackageIcon size={4} />;
	if (kind === "courier") return <BikeIcon size={4} />;
	return <HandIcon size={4} />;
};

export function ProductDetailsVendorSelectTrigger({
	product,
	viewModel,
	toggleVendorModal,
}: {
	product: ProductNoEdibleInfo;
	viewModel: OfferCardViewModel;
	toggleVendorModal: (open: boolean) => void;
}) {
	const address = viewModel.address;
	const addressLine1 = [address?.street, address?.postalCode]
		.filter(Boolean)
		.join(", ");

	const addressLine2City = address?.city;
	const addressLine2Distance = viewModel.distanceLabel;

	const hasAddressLine2 = Boolean(addressLine2City || addressLine2Distance);

	const deliveryBadges = viewModel.deliveryBadges ?? [];
	const showDiscount =
		viewModel.discount !== undefined &&
		viewModel.discount.originalPrice > 0 &&
		viewModel.discount.percentage > 0;

	return (
		<button
			type="button"
			className="cursor-pointer outline-none flex flex-col w-full rounded-xl border-2 border-gray-200 bg-white hover:bg-gray-100 transition-colors duration-300 ease-out p-2.5 text-left"
			onClick={() => toggleVendorModal(true)}
			data-testid="vendor-selection-trigger"
		>
			<div className="flex flex-col gap-1">
				<div className="flex justify-between items-start w-full gap-2">
					<div className="flex items-center gap-1 min-w-0">
						{viewModel.featured && (
							<TopPharmacyBadge data-testid="vendor-selection-trigger-top-badge" />
						)}
						<h3
							className="text-body-bold text-gray-700 line-clamp-1 text-left"
							data-testid="vendor-selection-trigger-text"
						>
							{viewModel.pharmacyName}
						</h3>
					</div>
					<div className="flex flex-col items-end flex-shrink-0">
						{viewModel.price !== undefined && (
							<Price
								variant="raw"
								className="text-body-bold text-ansay-700"
								price={viewModel.price}
								category={product.category}
								kind={product.kind}
							/>
						)}
					</div>
				</div>

				{(addressLine1 || hasAddressLine2 || showDiscount) && (
					<div className="flex justify-between items-start w-full gap-4">
						<div className="min-w-0 flex flex-col text-body-sm text-gray-500 text-left tracking-[-0.28px]">
							{addressLine1 && (
								<span
									className="truncate"
									data-testid="vendor-selection-trigger-address-line-1"
								>
									{addressLine1}
								</span>
							)}

							{hasAddressLine2 && (
								<span
									className="truncate"
									data-testid="vendor-selection-trigger-address-line-2"
								>
									{addressLine2City}
									{addressLine2City && addressLine2Distance && " "}
									{addressLine2Distance && (
										<span
											className="text-body-sm-emphasis text-gray-700"
											data-testid="vendor-selection-trigger-distance"
										>
											{addressLine2Distance}
										</span>
									)}
								</span>
							)}
						</div>

						{showDiscount && viewModel.discount && (
							<div
								className="flex items-center gap-2 flex-shrink-0"
								data-testid="vendor-selection-trigger-discount"
							>
								<div data-testid="vendor-selection-trigger-original-price">
									<Price
										variant="raw"
										className="text-body-sm text-gray-700 line-through"
										price={viewModel.discount.originalPrice}
										category={product.category}
										kind={product.kind}
									/>
								</div>
								<div
									className="px-2 py-1 rounded-md bg-amber-100 text-amber-900 text-caption"
									data-testid="vendor-selection-trigger-discount-percentage"
								>
									-{Math.round(viewModel.discount.percentage)}%
								</div>
							</div>
						)}
					</div>
				)}
			</div>

			<div className="flex justify-between items-center w-full mt-2">
				{deliveryBadges.length > 0 ? (
					<div className="flex flex-wrap gap-1">
						{deliveryBadges.map((badge) => (
							<div
								key={badge.kind}
								className={clsx(
									"inline-flex items-center gap-1 rounded bg-ansay-background text-caption text-ansay-700 h-7",
									badge.label ? "px-2" : "w-7 justify-center",
								)}
								data-testid={`vendor-selection-trigger-delivery-badge-${badge.kind}`}
							>
								{getDeliveryIcon(badge.kind)}
								{badge.label && <span>{badge.label}</span>}
							</div>
						))}
					</div>
				) : (
					<div />
				)}
				<ChevronRightIcon size={6} colorToken="gray-700" />
			</div>
		</button>
	);
}
